export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

export const logLevels = [
  LogLevel.DEBUG,
  LogLevel.INFO,
  LogLevel.WARN,
  LogLevel.ERROR,
] as const;

export interface LoggerOptions {
  logLevel: LogLevel;
}

export class Logger {
  private path: string[];
  private options: LoggerOptions;

  constructor(initialPath: string | string[], options: Partial<LoggerOptions>) {
    this.path = Array.isArray(initialPath)
      ? initialPath
      : initialPath
        ? [initialPath]
        : [];

    this.options = {
      logLevel: options.logLevel ?? LogLevel.INFO,
    };
  }

  /**
   * Extend the logger with a new service/component name
   * @param serviceName - The name of the service or component
   * @returns A new Logger instance with the extended path
   */
  extend(serviceName: string): Logger {
    return new Logger([...this.path, serviceName], this.options);
  }

  logLevel(level: LogLevel, message: string, ...metadata: unknown[]): void {
    if (logLevels.indexOf(level) >= logLevels.indexOf(this.options.logLevel)) {
      console[level](this.prefixMessage(message), ...metadata);
    }
  }

  trace(message: string, ...metadata: unknown[]): void {
    console.trace(this.prefixMessage(message), ...metadata);
  }

  /**
   * Log a message with the current service path
   * @param message - The log message
   * @param metadata - Additional metadata to log
   */
  info(message: string, ...metadata: unknown[]): void {
    this.logLevel(LogLevel.INFO, message, ...metadata);
  }

  /**
   * Log a warning message with the current service path
   * @param message - The warning message
   * @param metadata - Additional metadata to log
   */
  warn(message: string, ...metadata: unknown[]): void {
    this.logLevel(LogLevel.WARN, message, ...metadata);
  }

  /**
   * Log an error message with the current service path
   * @param message - The error message
   * @param metadata - Additional metadata to log
   */
  error(message: string, ...metadata: unknown[]): void {
    this.logLevel(LogLevel.ERROR, message, ...metadata);
  }

  /**
   * Log a debug message with the current service path
   * @param message - The debug message
   * @param metadata - Additional metadata to log
   */
  debug(message: string, ...metadata: unknown[]): void {
    this.logLevel(LogLevel.DEBUG, message, ...metadata);
  }

  /**
   * Generate the formatted prefix for log messages
   * @param message - The log message
   * @returns The formatted log prefix
   */
  prefixMessage(message: string): string {
    const pathString = this.getPath();
    return `${pathString} → ${message}`;
  }

  /**
   * Get the current path as a string
   * @returns The current service path
   */
  protected getPath(): string {
    return this.path.length > 0 ? this.path.join(" → ") : "FanfestSDK";
  }

  /**
   * Get the current path as an array
   * @returns The current service path as an array
   */
  protected getPathArray(): string[] {
    return [...this.path];
  }
}
